import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    RefreshCw, AlertTriangle, Check, X, Minus, RotateCcw, UserPlus, Search, Info, Trash2,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// MATRIZ DE ACESSO — conceder e tirar acesso pela tela.
//
// A outra metade desta aba (o *mapa*) é DERIVADA do roteador e só de leitura: ela
// conta rotas HTTP por perfil, e ninguém decide acesso com "Gerente: financeiro 2
// de 40 rotas". Esta metade fala a língua de quem administra — **card, aba, fila**
// — e é onde o acesso muda, sem deploy.
//
// ⚠️⚠️ A TELA NÃO REFAZ A CONTA. Cada célula vem pronta de
//    `GET /api/acessos/matriz`, do MESMO resolver que o servidor usa para negar.
//    Desenhar a célula por uma segunda regra aqui é como a matriz passa a
//    discordar do servidor — e ela existe justamente para ser a resposta
//    confiável.
//
// ⚠️ E ela NÃO PROTEGE NADA: quem nega o dado é o `authorizeCapacidade` em cada
//    rota. Esconder um card aqui sem a trava lá é cortina — o botão some e a URL
//    continua servindo.
// ---------------------------------------------------------------------------

// Os quatro estados de uma célula. O rótulo fala de CONSEQUÊNCIA, não de
// mecanismo: quem lê quer saber se a pessoa entra, não de onde veio a regra.
const ESTADOS = {
    'padrao-permitido': { rotulo: 'Entra',           cor: '#0f766e', fundo: '#f0fdfa', borda: '#99f6e4', Icone: Check },
    'padrao-negado':    { rotulo: 'Não entra',       cor: '#94a3b8', fundo: '#f8fafc', borda: '#e2e8f0', Icone: Minus },
    'liberado':         { rotulo: 'Liberado aqui',   cor: '#1d4ed8', fundo: '#eff6ff', borda: '#bfdbfe', Icone: Check },
    'bloqueado':        { rotulo: 'Bloqueado aqui',  cor: '#be123c', fundo: '#fff1f2', borda: '#fecdd3', Icone: X },
};

// Alterado pela tela (≠ veio do padrão do código). É o que a coluna "quem mudou"
// responderia numa tabela — aqui vira uma marca, porque a matriz é densa.
const ALTERADO = new Set(['liberado', 'bloqueado']);

const AdminAcessosMatriz = () => {
    const [dados, setDados]           = useState(null);
    const [modulo, setModulo]         = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro]             = useState('');
    const [edicao, setEdicao]         = useState(null); // { capacidade, rotulo, perfil, estado }
    const [nominal, setNominal]       = useState(null); // { capacidade, rotulo } — concessão por pessoa
    const [busca, setBusca]           = useState('');

    const token = localStorage.getItem('authToken');

    const carregar = useCallback(async (mod) => {
        setCarregando(true);
        setErro('');
        try {
            const url = mod ? `/api/acessos/matriz?modulo=${encodeURIComponent(mod)}` : '/api/acessos/matriz';
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Erro ao carregar a matriz.');
            setDados(data);
            // Na primeira carga, abre no primeiro módulo migrado — a matriz sem
            // módulo escolhido mostraria tudo e não caberia na tela.
            if (!mod && data.modulosMigrados?.length) setModulo(data.modulosMigrados[0]);
        } catch (e) {
            setErro(e.message || 'Erro de conexão.');
        } finally {
            setCarregando(false);
        }
    }, [token]);

    useEffect(() => { carregar(modulo); }, [modulo, carregar]);

    /** Grava (PERMITE/NEGA) ou volta ao padrão (DELETE). */
    const salvar = async ({ capacidade, alvoTipo, alvo, efeito, motivo }) => {
        const voltar = efeito === null;
        const res = await fetch('/api/acessos/concessao', {
            method: voltar ? 'DELETE' : 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(voltar ? { capacidade, alvoTipo, alvo } : { capacidade, alvoTipo, alvo, efeito, motivo }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Não foi possível salvar.');
        await carregar(modulo);
    };

    const perfisFiltrados = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        if (!dados?.perfis) return [];
        return termo ? dados.perfis.filter(p => p.perfil.toLowerCase().includes(termo)) : dados.perfis;
    }, [dados, busca]);

    // ESTILOS — espelham o resto do Painel Admin.
    const cartao = { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0' };
    const rotuloFiltro = { fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' };

    if (carregando && !dados) {
        return <div style={{ ...cartao, padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Carregando a matriz…</div>;
    }

    return (
        <div>
            {erro && (
                <div style={{ ...cartao, borderColor: '#fecdd3', backgroundColor: '#fff1f2', padding: '16px 20px', marginBottom: '20px', color: '#9f1239', fontSize: '13.5px' }}>
                    {erro}
                </div>
            )}

            {/* ⚠️⚠️ CACHE DEGRADADO É DESTAQUE, NÃO RODAPÉ: quando ele está assim, as
                concessões NÃO estão valendo (só os padrões do código) e cada clique
                aqui grava sem efeito. Sem este aviso, o Admin conclui que a tela
                está quebrada — ou pior, que o acesso mudou quando não mudou. */}
            {dados?.cache?.degradado && (
                <div style={{ ...cartao, borderColor: '#fed7aa', backgroundColor: '#fffbeb', padding: '18px 20px', marginBottom: '20px', display: 'flex', gap: '12px' }}>
                    <AlertTriangle size={20} color="#b45309" style={{ flexShrink: 0 }} />
                    <div style={{ fontSize: '13.5px', color: '#92400e', lineHeight: 1.55 }}>
                        <strong>As concessões não estão valendo agora.</strong> O servidor não conseguiu ler a
                        tabela e está usando só os padrões do código — o portal funciona, mas o que for
                        alterado aqui não terá efeito até isso normalizar.
                        <div style={{ marginTop: '6px', fontSize: '12.5px', opacity: 0.85 }}>
                            Motivo técnico: {dados.cache.erro || '—'}
                        </div>
                    </div>
                </div>
            )}

            {/* ⚠️ Concessão órfã nega em silêncio e RESSUSCITA se o nome for reusado:
                um perfil recriado com a mesma grafia herda a linha antiga. */}
            {dados?.orfas?.length > 0 && (
                <div style={{ ...cartao, borderColor: '#fecdd3', backgroundColor: '#fff1f2', padding: '18px 20px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                        <AlertTriangle size={18} color="#be123c" />
                        <strong style={{ fontSize: '13.5px', color: '#9f1239' }}>
                            {dados.orfas.length} concessão(ões) apontando para o nada
                        </strong>
                    </div>
                    <div style={{ fontSize: '12.5px', color: '#9f1239', lineHeight: 1.6, marginBottom: '10px' }}>
                        Elas não fazem efeito — mas <strong>voltam a valer</strong> se o nome for reusado.
                        Apague o que não for mais para valer.
                    </div>
                    {dados.orfas.map((o, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderTop: '1px solid #fecdd3', fontSize: '12.5px', color: '#9f1239' }}>
                            <code style={{ fontSize: '11.5px' }}>{o.capacidade}</code>
                            <span>· {o.alvoTipo} <strong>{o.alvo}</strong> · {o.motivo}</span>
                            <button
                                onClick={() => salvar({ capacidade: o.capacidade, alvoTipo: o.alvoTipo, alvo: o.alvo, efeito: null }).catch(e => setErro(e.message))}
                                title="Apagar esta concessão órfã"
                                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #fecdd3', backgroundColor: '#fff', color: '#be123c', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}
                            >
                                <Trash2 size={13} /> Apagar
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* COMO LER */}
            <div style={{ ...cartao, padding: '18px 20px', marginBottom: '20px', backgroundColor: '#f8fafc' }}>
                <div style={{ fontSize: '12px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>Como ler</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', fontSize: '12.5px', color: '#475569', alignItems: 'center' }}>
                    {Object.entries(ESTADOS).map(([id, e]) => (
                        <span key={id} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '6px', backgroundColor: e.fundo, border: `1px solid ${e.borda}`, color: e.cor }}>
                                <e.Icone size={13} strokeWidth={3} />
                            </span>
                            {e.rotulo}
                        </span>
                    ))}
                </div>
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', fontSize: '12.5px', color: '#475569', lineHeight: 1.65 }}>
                    ⚠️ <strong>“Entra” e “Não entra” vêm do padrão escrito no código</strong>; “Liberado” e
                    “Bloqueado” foram alterados por aqui. Voltar ao padrão <strong>apaga</strong> a exceção —
                    não grava outra por cima.<br />
                    ⚠️ O <strong>Admin</strong> não tem coluna: ele alcança tudo por <em>bypass</em>, e isso não se
                    edita por tela. As exceções (hoje a <strong>fila de Manutenção</strong> e o módulo
                    <strong> Projetos</strong>) são declaradas no código e ficam marcadas abaixo.<br />
                    ⚠️ <strong>Perfil novo nasce sem nada</strong> — é o desenho. Ele só passa a enxergar o que
                    for liberado aqui.
                </div>
            </div>

            {/* CONTROLES */}
            <div style={{ ...cartao, padding: '16px 20px', marginBottom: '20px', display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                    <div style={rotuloFiltro}>Módulo</div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {(dados?.modulosMigrados || []).map(m => (
                            <button
                                key={m}
                                onClick={() => setModulo(m)}
                                style={{
                                    padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                                    textTransform: 'capitalize',
                                    border: `1px solid ${modulo === m ? '#8b5cf6' : '#e2e8f0'}`,
                                    backgroundColor: modulo === m ? '#f5f3ff' : '#fff',
                                    color: modulo === m ? '#6d28d9' : '#64748b',
                                }}
                            >{m}</button>
                        ))}
                    </div>
                </div>
                <div style={{ flex: 1, minWidth: '220px' }}>
                    <div style={rotuloFiltro}>Buscar perfil</div>
                    <div style={{ position: 'relative' }}>
                        <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            placeholder="gerente, diretoria…"
                            style={{ width: '100%', padding: '9px 12px 9px 34px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                        />
                    </div>
                </div>
                <button
                    onClick={() => carregar(modulo)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                >
                    <RefreshCw size={15} /> Atualizar
                </button>
            </div>

            {/* ⚠️⚠️ A FRONTEIRA PRECISA ESTAR ESCRITA NA TELA. A matriz cobre só os
                módulos migrados; o resto do portal segue governado pelo código.
                Omitir isso faria a tela mentir por omissão sobre os demais módulos —
                alguém procuraria o Financeiro aqui e concluiria que ele não tem
                regra nenhuma. */}
            <div style={{ ...cartao, padding: '14px 18px', marginBottom: '20px', backgroundColor: '#f0f9ff', borderColor: '#bae6fd', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <Info size={17} color="#0369a1" style={{ flexShrink: 0, marginTop: '1px' }} />
                <div style={{ fontSize: '12.5px', color: '#075985', lineHeight: 1.6 }}>
                    A matriz governa <strong>{(dados?.modulosMigrados || []).join(', ') || '—'}</strong>.
                    Os demais módulos do portal continuam com o acesso escrito no código — para vê-los,
                    use as visões <strong>Por módulo</strong> e <strong>Por perfil</strong> do mapa.
                </div>
            </div>

            {/* A MATRIZ — linhas = perfis, colunas = capacidades.
                ⚠️ É de propósito, e não o contrário: a pergunta que se faz é "o que
                   este perfil alcança?", e são ~26 perfis contra poucas capacidades
                   por módulo. Perfis nas colunas dariam uma tabela larga demais para
                   ler sem rolagem horizontal. */}
            <div style={{ ...cartao, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc' }}>
                                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', position: 'sticky', left: 0, backgroundColor: '#f8fafc', zIndex: 1 }}>
                                    Perfil
                                </th>
                                {(dados?.capacidades || []).map(c => (
                                    <th key={c.chave} title={c.descricao} style={{ padding: '12px 10px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#334155', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #f1f5f9', minWidth: '92px' }}>
                                        {c.rotulo}
                                        <div style={{ fontSize: '10.5px', fontWeight: '600', color: '#94a3b8', marginTop: '3px' }}>
                                            {c.tipo === 'executar' ? 'executar' : 'ver'}
                                            {/* ⚠️ A marca da exceção ao bypass fica VISÍVEL: é ela que
                                                explica por que o Admin não alcança esta coluna. */}
                                            {!c.bypassAdmin && <span title="O Admin NÃO alcança esta capacidade por bypass — é exceção declarada no código"> · sem bypass</span>}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {perfisFiltrados.map(p => (
                                <tr key={p.perfil}>
                                    <td style={{ padding: '12px 20px', fontSize: '13.5px', borderBottom: '1px solid #f1f5f9', position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1, whiteSpace: 'nowrap' }}>
                                        <strong style={{ color: '#0f172a', fontWeight: '600' }}>{p.perfil}</strong>
                                        {/* ⚠️ Perfil derivado entra pelo perfil-base nos módulos do
                                            recorte dele. Sem dizer isso, a linha "não entra" de um
                                            derivado que ENTRA parece erro da tela. */}
                                        {p.derivado && (
                                            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>herda de {p.herdaDe}</div>
                                        )}
                                    </td>
                                    {(dados?.capacidades || []).map(c => {
                                        const celula = c.celulas[p.perfil] || { estado: 'padrao-negado' };
                                        const e = ESTADOS[celula.estado] || ESTADOS['padrao-negado'];
                                        return (
                                            <td key={c.chave} style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #f1f5f9', borderLeft: '1px solid #f8fafc' }}>
                                                <button
                                                    onClick={() => setEdicao({ capacidade: c.chave, rotulo: c.rotulo, perfil: p.perfil, estado: celula.estado, editavel: c.editavel })}
                                                    disabled={!c.editavel}
                                                    title={
                                                        `${e.rotulo}${celula.porHeranca ? ` · e entra por herança de ${celula.herdaDe}` : ''}`
                                                        + (c.editavel ? ' — clique para alterar' : ' — governada pelo código')
                                                    }
                                                    style={{
                                                        position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                        width: '32px', height: '32px', borderRadius: '8px',
                                                        backgroundColor: e.fundo, border: `1px solid ${e.borda}`, color: e.cor,
                                                        cursor: c.editavel ? 'pointer' : 'not-allowed', opacity: c.editavel ? 1 : 0.55,
                                                    }}
                                                >
                                                    <e.Icone size={15} strokeWidth={3} />
                                                    {/* Marca de "alterado por aqui" — o ponto some quando
                                                        a célula volta ao padrão. */}
                                                    {ALTERADO.has(celula.estado) && (
                                                        <span style={{ position: 'absolute', top: '-3px', right: '-3px', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: e.cor, border: '2px solid #fff' }} />
                                                    )}
                                                    {/* Entra por herança, mesmo sem estar na lista. */}
                                                    {celula.porHeranca && celula.estado === 'padrao-negado' && (
                                                        <span title={`Entra por herança de ${celula.herdaDe}`} style={{ position: 'absolute', bottom: '-2px', right: '1px', fontSize: '9px', fontWeight: '800', color: '#0f766e' }}>h</span>
                                                    )}
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* CONCESSÕES NOMINAIS — por pessoa, não por perfil.
                ⚠️ Elas existem porque perfil responde "o que essa pessoa é", e às
                   vezes a pergunta é "quem está tocando a implantação". É como a
                   fila de Manutenção funciona hoje. */}
            <div style={{ ...cartao, marginTop: '20px', padding: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Acesso por pessoa
                </div>
                <div style={{ fontSize: '12.5px', color: '#64748b', marginBottom: '14px', lineHeight: 1.6 }}>
                    Vence o perfil, nos dois sentidos — libera alguém sem liberar o perfil dele, ou bloqueia
                    uma pessoa sem mexer no resto.
                </div>
                {(dados?.capacidades || []).map(c => (
                    <div key={c.chave} style={{ padding: '12px 0', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>
                        <div style={{ minWidth: '180px' }}>
                            <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#0f172a' }}>{c.rotulo}</div>
                            {!c.bypassAdmin && (
                                <div style={{ fontSize: '11px', color: '#b45309', fontWeight: '700' }}>o Admin não entra por bypass</div>
                            )}
                        </div>
                        <div style={{ flex: 1, minWidth: '260px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                            {c.usuarios.length === 0 && (
                                <span style={{ fontSize: '12.5px', color: '#94a3b8' }}>ninguém — só vale o perfil</span>
                            )}
                            {c.usuarios.map(u => {
                                const e = ESTADOS[u.efeito === 'PERMITE' ? 'liberado' : 'bloqueado'];
                                return (
                                    <span key={u.id} title={u.motivo} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '6px 10px', borderRadius: '8px', backgroundColor: e.fundo, border: `1px solid ${e.borda}`, color: e.cor, fontSize: '12.5px', fontWeight: '600' }}>
                                        <e.Icone size={13} strokeWidth={3} />
                                        {u.nome}
                                        <button
                                            onClick={() => salvar({ capacidade: c.chave, alvoTipo: 'USUARIO', alvo: u.id, efeito: null }).catch(err => setErro(err.message))}
                                            title="Voltar ao padrão (apaga esta exceção)"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: e.cor, display: 'flex', padding: 0 }}
                                        ><X size={13} strokeWidth={3} /></button>
                                    </span>
                                );
                            })}
                            {c.editavel && (
                                <button
                                    onClick={() => setNominal({ capacidade: c.chave, rotulo: c.rotulo })}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', border: '1px dashed #cbd5e1', backgroundColor: '#fff', color: '#64748b', cursor: 'pointer', fontSize: '12.5px', fontWeight: '700' }}
                                >
                                    <UserPlus size={13} /> pessoa
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {edicao && (
                <PainelEdicao
                    alvo={edicao}
                    onFechar={() => setEdicao(null)}
                    onSalvar={async (efeito, motivo) => {
                        await salvar({ capacidade: edicao.capacidade, alvoTipo: 'PERFIL', alvo: edicao.perfil, efeito, motivo });
                        setEdicao(null);
                    }}
                />
            )}

            {nominal && (
                <PainelPessoa
                    alvo={nominal}
                    token={token}
                    onFechar={() => setNominal(null)}
                    onSalvar={async (id, efeito, motivo) => {
                        await salvar({ capacidade: nominal.capacidade, alvoTipo: 'USUARIO', alvo: id, efeito, motivo });
                        setNominal(null);
                    }}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Os dois painéis de edição.
//
// ⚠️⚠️ NÃO É UM TOGGLE NA CÉLULA, e isso é decisão: o MOTIVO é obrigatório. Seis
//    meses depois, a pergunta diante de uma exceção é "por que essa pessoa ganhou
//    isso?" — e um clique sem campo não deixa resposta nenhuma. O atrito aqui é o
//    ponto.
// ---------------------------------------------------------------------------
const Moldura = ({ titulo, subtitulo, children, onFechar }) => (
    <div
        onClick={onFechar}
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 50 }}
    >
        <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '460px', padding: '24px', boxShadow: '0 20px 60px rgba(15,23,42,0.25)' }}>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>{titulo}</h3>
            <p style={{ margin: '0 0 18px 0', fontSize: '13px', color: '#64748b' }}>{subtitulo}</p>
            {children}
        </div>
    </div>
);

const campoMotivo = {
    width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1',
    fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical',
};
const botao = (cor, fundo, borda) => ({
    flex: 1, padding: '10px 14px', borderRadius: '8px', border: `1px solid ${borda}`,
    backgroundColor: fundo, color: cor, cursor: 'pointer', fontWeight: '700', fontSize: '13px',
});

const PainelEdicao = ({ alvo, onFechar, onSalvar }) => {
    const [motivo, setMotivo]   = useState('');
    const [erro, setErro]       = useState('');
    const [salvando, setSalvando] = useState(false);
    const alterado = ALTERADO.has(alvo.estado);

    const acionar = async (efeito) => {
        setSalvando(true);
        setErro('');
        try { await onSalvar(efeito, motivo); }
        catch (e) { setErro(e.message); setSalvando(false); }
    };

    return (
        <Moldura titulo={alvo.rotulo} subtitulo={`Perfil: ${alvo.perfil} · hoje: ${ESTADOS[alvo.estado]?.rotulo}`} onFechar={onFechar}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                Por quê? <span style={{ color: '#be123c' }}>*</span>
            </label>
            <textarea
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                rows={3}
                placeholder="Ex.: a coordenação passou a acompanhar a fila da loja."
                style={campoMotivo}
            />
            <div style={{ fontSize: '11.5px', color: '#94a3b8', margin: '6px 0 16px 0' }}>
                É o que responde “por que essa pessoa ganhou isso?” seis meses depois.
            </div>

            {erro && <div style={{ fontSize: '12.5px', color: '#9f1239', marginBottom: '12px' }}>{erro}</div>}

            <div style={{ display: 'flex', gap: '8px' }}>
                <button disabled={salvando} onClick={() => acionar('PERMITE')} style={botao('#1d4ed8', '#eff6ff', '#bfdbfe')}>Liberar</button>
                <button disabled={salvando} onClick={() => acionar('NEGA')} style={botao('#be123c', '#fff1f2', '#fecdd3')}>Bloquear</button>
            </div>

            {/* ⚠️ Só aparece quando HÁ exceção: voltar ao padrão é apagar a linha, e
                oferecer isso numa célula que já é padrão só confundiria. */}
            {alterado && (
                <button
                    disabled={salvando}
                    onClick={async () => {
                        setSalvando(true);
                        try { await onSalvar(null, ''); }
                        catch (e) { setErro(e.message); setSalvando(false); }
                    }}
                    style={{ ...botao('#475569', '#f8fafc', '#e2e8f0'), width: '100%', marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}
                >
                    <RotateCcw size={14} /> Voltar ao padrão do código
                </button>
            )}
        </Moldura>
    );
};

const PainelPessoa = ({ alvo, token, onFechar, onSalvar }) => {
    const [usuarios, setUsuarios] = useState([]);
    const [termo, setTermo]       = useState('');
    const [escolhido, setEscolhido] = useState(null);
    const [motivo, setMotivo]     = useState('');
    const [erro, setErro]         = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch('/api/cadastro/listar-todos', { headers: { Authorization: `Bearer ${token}` } });
                const data = await res.json();
                if (res.ok) setUsuarios(Array.isArray(data) ? data : (data.usuarios || []));
            } catch { /* a busca simplesmente não oferece nomes — o ID ainda serve */ }
        })();
    }, [token]);

    const achados = useMemo(() => {
        const t = termo.trim().toLowerCase();
        if (!t) return [];
        return usuarios.filter(u => String(u.NOME_COMPLETO || '').toLowerCase().includes(t)).slice(0, 6);
    }, [termo, usuarios]);

    const acionar = async (efeito) => {
        setErro('');
        if (!escolhido) return setErro('Escolha a pessoa.');
        try { await onSalvar(escolhido.ID, efeito, motivo); }
        catch (e) { setErro(e.message); }
    };

    return (
        <Moldura titulo={alvo.rotulo} subtitulo="Acesso por pessoa — vence o perfil" onFechar={onFechar}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Quem</label>
            {escolhido ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
                    <strong style={{ fontSize: '13px', color: '#0f172a' }}>{escolhido.NOME_COMPLETO}</strong>
                    {/* ⚠️ O ID fica visível de propósito: é ele que vai para a tabela,
                        e ID solto não se audita sem o nome ao lado. */}
                    <span style={{ fontSize: '12px', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>#{escolhido.ID} · {escolhido.PERFIL}</span>
                    <button onClick={() => setEscolhido(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}><X size={15} /></button>
                </div>
            ) : (
                <>
                    <input
                        value={termo}
                        onChange={e => setTermo(e.target.value)}
                        placeholder="digite o nome…"
                        style={{ ...campoMotivo, marginBottom: '6px' }}
                    />
                    {achados.map(u => (
                        <button
                            key={u.ID}
                            onClick={() => { setEscolhido(u); setTermo(''); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: '8px', border: '1px solid #f1f5f9', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', color: '#0f172a', marginBottom: '4px' }}
                        >
                            {u.NOME_COMPLETO}
                            <span style={{ color: '#94a3b8', fontSize: '12px' }}> · {u.PERFIL} · {u.LOJA}</span>
                        </button>
                    ))}
                    <div style={{ height: '10px' }} />
                </>
            )}

            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                Por quê? <span style={{ color: '#be123c' }}>*</span>
            </label>
            <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} style={campoMotivo} />

            {erro && <div style={{ fontSize: '12.5px', color: '#9f1239', margin: '12px 0' }}>{erro}</div>}

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button onClick={() => acionar('PERMITE')} style={botao('#1d4ed8', '#eff6ff', '#bfdbfe')}>Liberar para ela</button>
                <button onClick={() => acionar('NEGA')} style={botao('#be123c', '#fff1f2', '#fecdd3')}>Bloquear para ela</button>
            </div>
        </Moldura>
    );
};

export default AdminAcessosMatriz;
